import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/contexts/LanguageContext';
import { OptimizedImage } from '@/components/OptimizedImage';
import { Sparkles, Building2, Calendar, Car } from 'lucide-react';
import { getBlurPlaceholder } from '@/utils/blurPlaceholders';

const WhyChooseSection: React.FC = () => {
    const { t } = useLanguage();

    const valuePropositions = [
        {
            id: 'premium',
            icon: Sparkles,
            title: t('activities.whyChoose.premium.title'),
            description: t('activities.whyChoose.premium.description'),
        },
        {
            id: 'allInOne',
            icon: Building2,
            title: t('activities.whyChoose.allInOne.title'),
            description: t('activities.whyChoose.allInOne.description'),
        },
        {
            id: 'flexible',
            icon: Calendar,
            title: t('activities.whyChoose.flexible.title'),
            description: t('activities.whyChoose.flexible.description'),
        },
        {
            id: 'easyAccess',
            icon: Car,
            title: t('activities.whyChoose.easyAccess.title'),
            description: t('activities.whyChoose.easyAccess.description'),
        },
    ];

    return (
        <section className="relative bg-elegant-white py-20 md:py-24 px-4 md:px-6 lg:px-0">
            {/* Gradient Transition from Activities Section */}
            <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-pebble-beige via-pebble-beige/50 to-elegant-white pointer-events-none" />
            
            {/* Split Layout: Image Left, Content Right */}
            <div className="relative grid grid-cols-1 lg:grid-cols-[40%_60%] gap-8 lg:gap-12 items-end">
                    {/* Left Column - Hero Image */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-100px' }}
                        transition={{ duration: 0.6 }}
                        className="relative w-full aspect-square lg:aspect-square rounded-2xl lg:rounded-none lg:rounded-r-2xl overflow-hidden"
                    >
                        <OptimizedImage
                            src="/images/activity arcade/activity-arcade-why-choose-us-mobile.webp"
                            srcSet="/images/activity arcade/activity-arcade-why-choose-us-mobile.webp 600w, /images/activity arcade/activity-arcade-why-choose-us-tablet.webp 900w, /images/activity arcade/activity-arcade-why-choose-us-desktop.webp 1200w"
                            sizes="(max-width: 640px) 600px, (max-width: 1024px) 900px, 1200px"
                            alt={t('activities.whyChoose.imageAlt')}
                            decoding="async"
                            width={1200}
                            height={1200}
                            className="w-full h-full object-contain"
                            blurDataURL={getBlurPlaceholder("/images/activity arcade/activity-arcade-why-choose-us-mobile.webp")}
                            fetchPriority="auto"
                        />
                    </motion.div>

                    {/* Right Column - Content */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true, margin: '-100px' }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="space-y-8 px-4 md:px-6 lg:px-8 lg:pr-20"
                    >
                        {/* Section Header */}
                        <div className="space-y-4">
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5 }}
                            >
                                <h2 className="text-3xl md:text-4xl lg:text-5xl font-playfair text-charcoal-gray mb-4 leading-tight">
                                    {t('activities.whyChoose.title')}
                                </h2>
                            </motion.div>
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <p className="text-lg md:text-xl text-charcoal-gray/80 font-lato leading-relaxed max-w-2xl">
                                    {t('activities.whyChoose.subtitle')}
                                </p>
                            </motion.div>
                        </div>

                        {/* Value Proposition Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                            {valuePropositions.map((prop, index) => {
                                const IconComponent = prop.icon;
                                return (
                                    <motion.div
                                        key={prop.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                                        className="group relative"
                                    >
                                        {/* Glassmorphic Card */}
                                        <div className="relative h-full p-6 rounded-2xl bg-white/80 backdrop-blur-sm border border-alpine-gold/20 shadow-lg hover:shadow-xl transition-all duration-300 hover:border-alpine-gold/40 overflow-hidden">
                                            {/* Gold accent line */}
                                            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-alpine-gold/50 via-alpine-gold to-alpine-gold/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                            
                                            {/* Icon */}
                                            <div className="w-14 h-14 mb-4 bg-alpine-gold/10 rounded-xl flex items-center justify-center group-hover:bg-alpine-gold/20 transition-colors duration-300">
                                                <IconComponent className="w-7 h-7 text-alpine-gold" />
                                            </div>

                                            {/* Content */}
                                            <h3 className="text-xl font-playfair text-charcoal-gray mb-3 group-hover:text-alpine-gold transition-colors duration-300">
                                                {prop.title}
                                            </h3>
                                            <p className="text-sm md:text-base text-charcoal-gray/70 font-lato leading-relaxed">
                                                {prop.description}
                                            </p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </motion.div>
            </div>
        </section>
    );
};

export default WhyChooseSection;

